import React, { useEffect, useState } from 'react';
import ProjectSummary from '../components/quote/QuoteGenerator';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import './ProjectSummaryPage.css';
import { FaPrint } from 'react-icons/fa';
import { FiInfo, FiMapPin, FiUser, FiChevronDown, FiChevronUp, FiEdit, FiCheckCircle, FiXCircle, FiSave, FiX } from 'react-icons/fi';

/**
 * Project Summary page that displays the project summary
 * 
 * @param {Object} props - Component props
 * @param {Object} props.sensorCounts - Counts of different sensor types
 * @param {string} props.projectName - Name of the project
 * @param {string} props.projectId - ID of the project for loading data from Supabase
 * @param {Function} props.onBack - Function to navigate back to previous page
 * @returns {JSX.Element} Project Summary Page component
 */
const ProjectSummaryPage = ({ sensorCounts, projectName, projectId, onBack }) => {
  const { user } = useAuth();
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [requirementsExpanded, setRequirementsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customer: '',
    location: '',
    installation_requirements: {
      unionLabor: false,
      fullInstall: true,
      selfInstallInfrastructure: false,
      selfInstallWaffle: false,
      afterHoursOnly: false,
      conduitRequired: false,
      jboxRequired: false,
      racewayConfirmed: true,
      bespokeDetails: ''
    }
  });

  // Fetch project data from Supabase if projectId is provided
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId || !user) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, error: fetchError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .eq('user_id', user.id)
          .single();
        
        if (fetchError) {
          throw fetchError;
        }
        
        // Handle both database schemas - either use floorplan_data (old schema)
        // or individual fields (new schema)
        let floorplanData;
        if (data.floorplan_data) {
          // Old schema
          floorplanData = data.floorplan_data;
          console.log('Using legacy floorplan_data field');
        } else {
          // New schema - construct from individual fields
          floorplanData = {
            boxes: data.boxes || [],
            shapes: data.shapes || [],
            entryPoints: data.entryPoints || data.entry_points || []
          };
          console.log('Using individual fields for floorplan data');
        }
        
        // Initialize installation_requirements if it doesn't exist
        const installation_requirements = data.installation_requirements || {
          unionLabor: false,
          fullInstall: true,
          selfInstallInfrastructure: false,
          selfInstallWaffle: false,
          afterHoursOnly: false,
          conduitRequired: false,
          jboxRequired: false,
          racewayConfirmed: true,
          bespokeDetails: ''
        };
        
        setProjectData({
          id: data.id,
          floorplanData: floorplanData,
          name: data.name,
          customer: data.customer || '',
          location: data.location || '',
          installation_requirements
        });
        
        // Initialize form data with project data
        setFormData({
          customer: data.customer || '',
          location: data.location || '',
          installation_requirements
        });
      } catch (err) {
        console.error('Error fetching project data:', err);
        setError('Failed to load project data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjectData();
  }, [projectId, user]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle checkbox changes for installation requirements
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      installation_requirements: {
        ...prev.installation_requirements,
        [name]: checked
      }
    }));
  };

  // Handle text input changes for installation requirements
  const handleInstallationTextChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      installation_requirements: {
        ...prev.installation_requirements,
        [name]: value
      }
    }));
  };

  // Save changes to the project
  const handleSave = async () => {
    if (!projectData?.id) return;
    
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          customer: formData.customer,
          location: formData.location,
          installation_requirements: formData.installation_requirements
        })
        .eq('id', projectData.id);
      
      if (updateError) throw updateError;
      
      // Update local project data
      setProjectData(prev => ({
        ...prev,
        customer: formData.customer,
        location: formData.location,
        installation_requirements: formData.installation_requirements
      }));
      
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating project details:', err);
      alert('Failed to save project details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    // Reset form data to current project data
    setFormData({
      customer: projectData?.customer || '',
      location: projectData?.location || '',
      installation_requirements: projectData?.installation_requirements || {
        unionLabor: false,
        fullInstall: true,
        selfInstallInfrastructure: false,
        selfInstallWaffle: false,
        afterHoursOnly: false,
        conduitRequired: false,
        jboxRequired: false,
        racewayConfirmed: true,
        bespokeDetails: ''
      }
    });
    setIsEditing(false);
  };

  // If not authenticated, show message
  if (!user) {
    return (
      <div className="summary-page">
        <div className="summary-content-full">
          <div className="summary-auth-message">
            <h2>Project Summary</h2>
            <p>Please log in to access project summaries.</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <div className="summary-page">
        <div className="summary-content-full">
          <div className="summary-loading">
            <h2>Loading Project Data...</h2>
          </div>
        </div>
      </div>
    );
  }
  
  // Show error message if there was an error
  if (error) {
    return (
      <div className="summary-page">
        <div className="summary-content-full">
          <div className="summary-error">
            <h2>Error</h2>
            <p>{error}</p>
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Use project data from Supabase if available, otherwise use props
  const effectiveProjectName = projectData?.name || projectName;
  const floorplanData = projectData?.floorplanData;

  // Handle printing the summary
  const printSummary = () => {
    window.print();
  };

  // Render installation requirements details in view mode
  const renderInstallationRequirements = () => {
    if (!projectData?.installation_requirements) return null;
    
    const req = projectData.installation_requirements;
    
    return (
      <div className="installation-requirements">
        <div className="installation-item">
          <div className="installation-label">
            {req.unionLabor ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>Union Labor:</span>
          </div>
          <div className="installation-value">
            {req.unionLabor ? 'Required' : 'Not Required'}
          </div>
        </div>
        
        <div className="installation-item">
          <div className="installation-label">
            {req.fullInstall ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>Installation Type:</span>
          </div>
          <div className="installation-value">
            {req.fullInstall ? 'Full Installation (Cable + Last Mile)' : 'Last Mile Only'}
          </div>
        </div>
        
        {req.fullInstall && (
          <div className="installation-item nested">
            <div className="installation-label">
              {req.selfInstallWaffle ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
              <span>Waffle Installation:</span>
            </div>
            <div className="installation-value">
              {req.selfInstallWaffle ? 'Self-install' : 'Romulus will install'}
            </div>
          </div>
        )}
        
        <div className="installation-item">
          <div className="installation-label">
            {req.afterHoursOnly ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>Work Hours:</span>
          </div>
          <div className="installation-value">
            {req.afterHoursOnly ? 'After Hours Only' : 'Business Hours'}
          </div>
        </div>
        
        <div className="installation-item">
          <div className="installation-label">
            {req.conduitRequired ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>Conduit:</span>
          </div>
          <div className="installation-value">
            {req.conduitRequired ? 'Required' : 'Not Required'}
          </div>
        </div>
        
        <div className="installation-item">
          <div className="installation-label">
            {req.jboxRequired ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>J-Box:</span>
          </div>
          <div className="installation-value">
            {req.jboxRequired ? 'Required' : 'Not Required'}
          </div>
        </div>
        
        <div className="installation-item">
          <div className="installation-label">
            {req.racewayConfirmed ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>For Waffle Ceiling:</span>
          </div>
          <div className="installation-value">
            {req.racewayConfirmed ? 'Standard Raceway is Sufficient' : 'Bespoke Solution Required'}
          </div>
        </div>
        
        <div className="installation-item">
          <div className="installation-label">
            {req.quoteQAd ? <FiCheckCircle className="checked" /> : <FiXCircle className="unchecked" />}
            <span>Quote QA Status:</span>
          </div>
          <div className="installation-value">
            {req.quoteQAd ? 'QA Completed' : 'Pending QA'}
          </div>
        </div>
        
        {!req.racewayConfirmed && req.bespokeDetails && (
          <div className="installation-item nested">
            <div className="installation-label">
              <span>Bespoke Details:</span>
            </div>
            <div className="installation-value bespoke-details">
              {req.bespokeDetails}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render installation requirements form in edit mode
  const renderInstallationRequirementsForm = () => {
    const req = formData.installation_requirements;
    
    return (
      <div className="installation-requirements-form">
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="unionLabor"
              checked={req.unionLabor}
              onChange={handleCheckboxChange}
            />
            <span className="installation-label-text">Union Labor Required</span>
          </label>
        </div>
        
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="fullInstall"
              checked={req.fullInstall}
              onChange={handleCheckboxChange}
            />
            <span className="installation-label-text">Full Installation (Cable + Last Mile)</span>
          </label>
        </div>
        
        {req.fullInstall && (
          <div className="form-group nested">
            <label>
              <input
                type="checkbox"
                name="selfInstallWaffle"
                checked={req.selfInstallWaffle}
                onChange={handleCheckboxChange}
              />
              <span className="installation-label-text">Self-install (Waffle)</span>
            </label>
          </div>
        )}
        
        {!req.fullInstall && (
          <div className="form-group nested">
            <label>
              <input
                type="checkbox"
                name="selfInstallInfrastructure"
                checked={req.selfInstallInfrastructure}
                onChange={handleCheckboxChange}
              />
              <span className="installation-label-text">Customer's cable vendor will install infrastructure</span>
            </label>
          </div>
        )}
        
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="afterHoursOnly"
              checked={req.afterHoursOnly}
              onChange={handleCheckboxChange}
            />
            <span className="installation-label-text">After hours work only</span>
          </label>
        </div>
        
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="conduitRequired"
              checked={req.conduitRequired}
              onChange={handleCheckboxChange}
            />
            <span className="installation-label-text">Conduit Required</span>
          </label>
        </div>
        
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="jboxRequired"
              checked={req.jboxRequired}
              onChange={handleCheckboxChange}
            />
            <span className="installation-label-text">J-Box Required</span>
          </label>
        </div>
        
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="racewayConfirmed"
              checked={req.racewayConfirmed}
              onChange={handleCheckboxChange}
            />
            <span className="installation-label-text">For Waffle Ceiling: Standard Raceway is Sufficient</span>
          </label>
        </div>
        
        {!req.racewayConfirmed && (
          <div className="form-group nested">
            <label>
              <span className="installation-label-text">Bespoke Details:</span>
            </label>
            <textarea
              name="bespokeDetails"
              value={req.bespokeDetails}
              onChange={handleInstallationTextChange}
              placeholder="Please provide details about the bespoke installation requirements"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="summary-page">
      <div className="summary-content-full">
        <div className="summary-header">
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>
        
        {/* Project Details Section */}
        <div className="project-details-section">
          <div className="section-header">
            <div className="section-title" onClick={() => setDetailsExpanded(!detailsExpanded)}>
              <h2>Project Details</h2>
              {detailsExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {!isEditing ? (
              <button className="edit-button" onClick={() => setIsEditing(true)}>
                <FiEdit /> Edit
              </button>
            ) : (
              <div className="edit-actions">
                <button className="save-button" onClick={handleSave} disabled={saving}>
                  <FiSave /> {saving ? 'Saving...' : 'Save'}
                </button>
                <button className="cancel-button" onClick={handleCancelEdit}>
                  <FiX /> Cancel
                </button>
              </div>
            )}
          </div>
          
          {detailsExpanded && (
            <div className="project-details-content">
              {!isEditing ? (
                <>
                  <div className="detail-item">
                    <div className="detail-icon"><FiUser /></div>
                    <div className="detail-label">Customer:</div>
                    <div className="detail-value">{projectData?.customer || '-'}</div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-icon"><FiMapPin /></div>
                    <div className="detail-label">Location:</div>
                    <div className="detail-value">{projectData?.location || '-'}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="customer">Customer:</label>
                    <input
                      type="text"
                      id="customer"
                      name="customer"
                      value={formData.customer}
                      onChange={handleChange}
                      placeholder="Enter customer name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="location">Location:</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Enter location"
                    />
                  </div>
                </>
              )}
              
              <div className="installation-section">
                <div className="section-subheader" onClick={() => setRequirementsExpanded(!requirementsExpanded)}>
                  <h3>Installation Requirements</h3>
                  {requirementsExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                
                {requirementsExpanded && (
                  isEditing ? renderInstallationRequirementsForm() : renderInstallationRequirements()
                )}
              </div>
            </div>
          )}
        </div>
        
        <ProjectSummary
          sensorCounts={sensorCounts || { entry: 1, oa: 2, waffleMeeting: 1, waffleDesk: 3 }} 
          projectName={effectiveProjectName}
          onClose={onBack}
          isPageMode={true}
          floorplanData={floorplanData}
        />
      </div>
    </div>
  );
};

export default ProjectSummaryPage; 